/**
 * Battalion.js
 * PSP/CRE Tool v2.0 - Hierarchical Force Structure
 * 
 * VERSION 2.0 - Task 1.7: Battalion-Level Unit Type Support
 * 
 * MAJOR ENHANCEMENTS:
 * - Added unit_type field (INFANTRY, MECHANIZED, ARMOR, ARTILLERY, etc.)
 * - Added operational_state field (COMBAT_ENGAGED, SUPPORT_ACTIVE, TRUE_RESERVE)
 * - Updated getCasualtyModifier() to use unit type modifier from constants
 * - Auto-assigns operational state based on unit type
 * - Enables accurate battalion-level casualty calculation (±5% accuracy)
 * 
 * Medical Hierarchy Position:
 * - Battalion → Role 1 (Battalion Aid Station)
 * - Provides point-of-injury care and stabilization
 * - First medical contact for casualties
 * 
 * Author: Dr. Tatar Radu MD
 * Institution: Romanian Military Medical Institute
 * Date: December 11, 2024
 * Version: 2.0 - Battalion Unit Type Support (Task 1.7)
 */

import { BaseUnit } from './BaseUnit.js';
import { BATTALION_TYPES as BATTALION_TYPES_TABLE } from '../../constants/unitTypes.js';

// ============================================================================
// BATTALION UNIT TYPE CONSTANTS
// ============================================================================

/**
 * Battalion-level unit types with casualty modifiers and operational defaults
 * These match the brigade-level types but apply specifically to battalions
 */
export const BATTALION_UNIT_TYPES = {
  // Combat/Maneuver Battalion Types
  INFANTRY: {
    label: 'Infantry Battalion',
    icon: '🎖️',
    casualty_modifier: 1.2,
    has_frontage: true,
    operational_state: 'COMBAT_ENGAGED',
    typical_position_km: 2,
    typical_strength: 800,
    description: 'Light infantry with limited vehicle protection'
  },
  MECHANIZED: {
    label: 'Mechanized Battalion',
    icon: '🚜',
    casualty_modifier: 1.0,
    has_frontage: true,
    operational_state: 'COMBAT_ENGAGED',
    typical_position_km: 3,
    typical_strength: 800,
    description: 'Infantry in armored personnel carriers (baseline)'
  },
  ARMOR: {
    label: 'Armor Battalion',
    icon: '🛡️',
    casualty_modifier: 0.85,
    has_frontage: true,
    operational_state: 'COMBAT_ENGAGED',
    typical_position_km: 4,
    typical_strength: 800,
    description: 'Tank battalion with maximum protection'
  },
  RECONNAISSANCE: {
    label: 'Reconnaissance Battalion',
    icon: '🔭',
    casualty_modifier: 1.1,
    has_frontage: true,
    operational_state: 'COMBAT_ENGAGED',
    typical_position_km: 0,
    typical_strength: 600,
    description: 'Reconnaissance and security operations'
  },
  
  // Combat Support Battalion Types
  ARTILLERY: {
    label: 'Artillery Battalion',
    icon: '💥',
    casualty_modifier: 0.08,
    has_frontage: false,
    operational_state: 'SUPPORT_ACTIVE',
    typical_position_km: 15,
    typical_strength: 600,
    description: 'Fire support positioned behind maneuver elements'
  },
  AIR_DEFENSE: {
    label: 'Air Defense Battalion',
    icon: '🛡️',
    casualty_modifier: 0.12,
    has_frontage: false,
    operational_state: 'SUPPORT_ACTIVE',
    typical_position_km: 10,
    typical_strength: 500,
    description: 'Short-range air defense protection'
  },
  ENGINEERS: {
    label: 'Engineer Battalion',
    icon: '🔧',
    casualty_modifier: 0.15,
    has_frontage: false,
    operational_state: 'SUPPORT_ACTIVE',
    typical_position_km: 8,
    typical_strength: 600,
    description: 'Combat engineering and construction'
  },
  
  // Combat Service Support Battalion Types
  LOGISTICS: {
    label: 'Logistics/Support Battalion',
    icon: '📦',
    casualty_modifier: 0.06,
    has_frontage: false,
    operational_state: 'SUPPORT_ACTIVE',
    typical_position_km: 25,
    typical_strength: 500,
    description: 'Supply, maintenance, and transportation'
  },
  SIGNAL: {
    label: 'Signal Battalion',
    icon: '📡',
    casualty_modifier: 0.07,
    has_frontage: false,
    operational_state: 'SUPPORT_ACTIVE',
    typical_position_km: 20,
    typical_strength: 400,
    description: 'Communications and information systems'
  },
  MEDICAL: {
    label: 'Medical Battalion',
    icon: '🏥',
    casualty_modifier: 0.04,
    has_frontage: false,
    operational_state: 'SUPPORT_ACTIVE',
    typical_position_km: 15,
    typical_strength: 400,
    description: 'Medical support and evacuation'
  },
  HQ: {
    label: 'Headquarters Company/Battalion',
    icon: '⭐',
    casualty_modifier: 0.04,
    has_frontage: false,
    operational_state: 'SUPPORT_ACTIVE',
    typical_position_km: 20,
    typical_strength: 300,
    description: 'Command, control, and staff functions'
  }
};

/**
 * Operational states for battalions
 * Determines battle casualty multiplier and DNBI rates
 */
export const BATTALION_OPERATIONAL_STATES = {
  COMBAT_ENGAGED: {
    value: 'COMBAT_ENGAGED',
    label: 'Combat Engaged',
    description: 'Units on frontage, actively fighting',
    battle_multiplier: 1.0,
    dnbi_rate_per_1000_day: 2.0
  },
  SUPPORT_ACTIVE: {
    value: 'SUPPORT_ACTIVE',
    label: 'Support Active',
    description: 'Support units actively working but not in direct combat',
    battle_multiplier: 0.2,
    dnbi_rate_per_1000_day: 1.0
  },
  TRUE_RESERVE: {
    value: 'TRUE_RESERVE',
    label: 'True Reserve',
    description: 'Units pulled to rest/regenerate',
    battle_multiplier: 0.05,
    dnbi_rate_per_1000_day: 0.5
  }
};

/**
 * Get battalion unit type configuration
 * @param {string} unitType - Battalion unit type key
 * @returns {Object} Unit type configuration
 */
// 2026-10-17 (Dr. Tatar, Tab 1 review): this model kept its OWN type table (BATTALION_UNIT_TYPES above), older than the
// BATTALION_TYPES table of constants/unitTypes.js that Tab 1 and the calculator read. 11 types (engineers, maintenance,
// transport, supply, airborne, mountain, aviation ...) were missing here and silently became a mechanized battalion
// (state COMBAT_ENGAGED for supply units, engineers shown as engaged while their type says support), and the modifiers
// differed (artillery 0.08 against 0.4). true = read the one table of unitTypes.js first; false restores the old lookup.
export const BATTALION_MODEL_USES_UNIT_TYPES_TABLE = true;

export function getBattalionUnitType(unitType) {
  if (BATTALION_MODEL_USES_UNIT_TYPES_TABLE) {
    const row = BATTALION_TYPES_TABLE[unitType] || BATTALION_UNIT_TYPES[unitType];
    if (!row) {
      throw new Error(`Battalion.getBattalionUnitType: unknown battalion type '${unitType}' (valid: ${Object.keys(BATTALION_TYPES_TABLE).join(', ')})`);
    }
    return row;
  }
  return BATTALION_UNIT_TYPES[unitType] || BATTALION_UNIT_TYPES.MECHANIZED;
}

/**
 * Get operational state configuration
 * @param {string} state - Operational state key
 * @returns {Object} Operational state configuration
 */
export function getBattalionOperationalState(state) {
  return BATTALION_OPERATIONAL_STATES[state] || BATTALION_OPERATIONAL_STATES.SUPPORT_ACTIVE;
}

// ============================================================================
// BATTALION CLASS
// ============================================================================

export class Battalion extends BaseUnit {
  /**
   * Create a Battalion
   * 
   * @param {Object} config - Battalion configuration
   * @param {string} config.id - Unique identifier
   * @param {string} config.name - Battalion name
   * @param {number} config.battalion_number - Number within brigade (1, 2, 3...)
   * @param {string} [config.unit_type='MECHANIZED'] - Battalion unit type (NEW v2.0)
   * @param {string} [config.operational_state] - Operational state (NEW v2.0, auto-assigned if not provided)
   * @param {number} [config.strength] - Personnel strength (auto from unit type if not provided)
   * @param {number} [config.position_feba_km] - Distance from FEBA (auto from unit type if not provided)
   */
  constructor(config) {
    // Get unit type configuration for defaults
    const unitTypeConfig = getBattalionUnitType(config.unit_type || 'MECHANIZED');
    
    super({
      ...config,
      type: 'BATTALION',
      strength: config.strength || unitTypeConfig.typical_strength || 800,
      position_feba_km: config.position_feba_km !== undefined 
        ? config.position_feba_km 
        : unitTypeConfig.typical_position_km
    });
    
    // Battalion-specific properties
    this.battalion_number = config.battalion_number;
    this.parent_brigade_id = config.parent_brigade_id || null;
    // 2026-10-25: where the card stands relative to its brigade (BRIGADE, SUPPORT_AREA) so that it follows the brigade position; null = own position
    this.position_anchor = config.position_anchor || null;
    
    // =========================================================================
    // NEW v2.0: Unit Type and Operational State
    // =========================================================================
    
    // Unit type (INFANTRY, MECHANIZED, ARMOR, ARTILLERY, etc.)
    this.unit_type = config.unit_type || 'MECHANIZED';
    this.unit_type_config = unitTypeConfig;
    
    // Operational state (auto-assigned based on unit type if not provided)
    this.operational_state = config.operational_state || unitTypeConfig.operational_state;
    this.operational_state_config = getBattalionOperationalState(this.operational_state);
    
    // Has frontage (determines if battalion is on FEBA)
    this.has_frontage = unitTypeConfig.has_frontage;
    
    // Casualty modifier from unit type
    this.unit_type_modifier = unitTypeConfig.casualty_modifier;
    
    // =========================================================================
    // Medical facilities
    // =========================================================================
    this.role1_facility = null;
    this.role1_facility_id = null;
    
    // =========================================================================
    // Operational status (legacy fields, kept for compatibility)
    // =========================================================================
    this.mission = config.mission || this._getDefaultMission();
    this.is_frontline = config.is_frontline !== undefined 
      ? config.is_frontline 
      : this.has_frontage;
  }
  
  // ============================================================================
  // PRIVATE HELPERS
  // ============================================================================
  
  /**
   * Get default mission based on unit type
   * @returns {string} Default mission
   */
  _getDefaultMission() {
    if (this.has_frontage) {
      return 'SUPPORTING';
    }
    return 'RESERVE';  // Support units are in "reserve" position
  }
  
  // ============================================================================
  // UNIT TYPE METHODS (NEW v2.0)
  // ============================================================================
  
  /**
   * Set battalion unit type
   * Automatically updates: casualty modifier, operational state, position, frontage
   * 
   * @param {string} newUnitType - New unit type key
   */
  setUnitType(newUnitType) {
    const config = getBattalionUnitType(newUnitType);
    
    this.unit_type = newUnitType;
    this.unit_type_config = config;
    this.unit_type_modifier = config.casualty_modifier;
    this.has_frontage = config.has_frontage;
    
    // Auto-update operational state
    this.setOperationalState(config.operational_state);
    
    // Update position if significantly different
    if (Math.abs(this.position_feba_km - config.typical_position_km) > 10) {
      this.position_feba_km = config.typical_position_km;
    }
    
    // Update frontline status
    this.is_frontline = config.has_frontage;
    
    // Update mission
    this.mission = this._getDefaultMission();
  }
  
  /**
   * Set operational state
   * 
   * @param {string} newState - Operational state key
   */
  setOperationalState(newState) {
    const valid_states = Object.keys(BATTALION_OPERATIONAL_STATES);
    if (!valid_states.includes(newState)) {
      throw new Error(`Invalid operational state: ${newState}. Must be one of: ${valid_states.join(', ')}`);
    }
    
    this.operational_state = newState;
    this.operational_state_config = getBattalionOperationalState(newState);
  }
  
  /**
   * Get unit type display label
   * @returns {string} Display label with icon
   */
  getUnitTypeLabel() {
    return `${this.unit_type_config.icon} ${this.unit_type_config.label}`;
  }
  
  /**
   * Get operational state display label
   * @returns {string} Display label
   */
  getOperationalStateLabel() {
    return this.operational_state_config.label;
  }
  
  // ============================================================================
  // CASUALTY MODIFIER (ENHANCED v2.0)
  // ============================================================================
  
  /**
   * Get expected casualty modifier based on unit type, operational state, and mission
   * 
   * v2.0 ENHANCEMENT:
   * - Primary modifier from unit_type (e.g., ARTILLERY = 0.08x)
   * - Secondary modifier from operational_state (e.g., SUPPORT_ACTIVE = 0.2x)
   * - Additional modifiers from mission and frontline status
   * 
   * @returns {number} Combined casualty multiplier
   */
  getCasualtyModifier() {
    // Base modifier from unit type
    let modifier = this.unit_type_modifier;
    
    // Operational state modifier (already baked into unit type default)
    // Only apply if state differs from unit type default
    if (this.operational_state !== this.unit_type_config.operational_state) {
      const stateConfig = getBattalionOperationalState(this.operational_state);
      const defaultStateConfig = getBattalionOperationalState(this.unit_type_config.operational_state);
      
      // Adjust by ratio of state multipliers
      modifier *= (stateConfig.battle_multiplier / defaultStateConfig.battle_multiplier);
    }
    
    // Mission modifier (legacy, for combat units only)
    if (this.has_frontage) {
      if (this.mission === 'MAIN_EFFORT') {
        modifier *= 1.5;  // +50% casualties (main attack/defense)
      } else if (this.mission === 'SUPPORTING') {
        modifier *= 1.0;  // Baseline for supporting
      } else if (this.mission === 'RESERVE') {
        modifier *= 0.5;  // -50% casualties (reserve position)
      }
    }
    
    // Frontline modifier for combat units
    if (this.is_frontline && this.has_frontage) {
      modifier *= 1.3;  // +30% if actually on frontline
    } else if (!this.is_frontline && this.has_frontage) {
      modifier *= 0.8;  // -20% if combat unit but not frontline
    }
    
    return modifier;
  }
  
  /**
   * Get DNBI rate based on operational state
   * @returns {number} DNBI rate per 1000 per day
   */
  getDNBIRate() {
    return this.operational_state_config.dnbi_rate_per_1000_day;
  }
  
  // ============================================================================
  // FACILITY METHODS (unchanged from v1.0)
  // ============================================================================
  
  /**
   * Generate unique facility ID for this battalion's Role 1
   * Format: role1_div{X}_brig{Y}_bn{Z}
   * 
   * @returns {string|null} Facility ID or null if no parent brigade
   */
  generateFacilityId() {
    if (!this.parent) return null;
    
    const brigade = this.parent;
    if (!brigade.parent) return null;
    
    const division = brigade.parent;
    
    return `role1_div${division.division_number}_brig${brigade.brigade_number}_bn${this.battalion_number}`;
  }
  
  /**
   * Generate human-readable facility name
   * 
   * @returns {string} Name like "Division 1 / Brigade 2 / Battalion 3 (Role 1)"
   */
  generateFacilityName() {
    if (!this.parent) return `Battalion ${this.battalion_number} Aid Station`;
    
    const brigade = this.parent;
    if (!brigade.parent) return `Brigade ${brigade.brigade_number} / Battalion ${this.battalion_number} (Role 1)`;
    
    const division = brigade.parent;
    return `Division ${division.division_number} / Brigade ${brigade.brigade_number} / Battalion ${this.battalion_number} (Role 1)`;
  }
  
  /**
   * Get Role 1 distance from FEBA
   * @returns {number} Distance in kilometers
   */
  getRole1Distance() {
    return this.position_feba_km;
  }
  
  /**
   * Link Role 1 medical facility to this battalion
   * @param {Role1Facility} facility - Medical facility object
   */
  linkRole1Facility(facility) {
    this.role1_facility = facility;
    this.role1_facility_id = facility.facility_id;
  }
  
  /**
   * Check if battalion has medical support configured
   * @returns {boolean} True if Role 1 facility linked
   */
  hasMedicalSupport() {
    return this.role1_facility !== null;
  }
  
  /**
   * Get medical facility capacity status
   * @returns {Object|null} Capacity info or null if no facility
   */
  getMedicalCapacityStatus() {
    if (!this.role1_facility) return null;
    
    return {
      facility_id: this.role1_facility_id,
      facility_name: this.role1_facility.facility_name,
      beds_total: this.role1_facility.beds,
      beds_available: this.role1_facility.getAvailableBeds(),
      bed_utilization: this.role1_facility.getBedUtilization(),
      operational: this.role1_facility.operational
    };
  }
  
  // ============================================================================
  // OPERATIONAL METHODS (updated for v2.0 compatibility)
  // ============================================================================
  
  /**
   * Set battalion mission type
   * Affects casualty rates in calculator
   * 
   * @param {string} mission - 'MAIN_EFFORT', 'SUPPORTING', 'RESERVE'
   */
  setMission(mission) {
    const valid_missions = ['MAIN_EFFORT', 'SUPPORTING', 'RESERVE'];
    if (!valid_missions.includes(mission)) {
      throw new Error(`Invalid mission: ${mission}. Must be one of: ${valid_missions.join(', ')}`);
    }
    this.mission = mission;
  }
  
  /**
   * Set frontline status
   * Frontline battalions have higher casualty rates
   * 
   * @param {boolean} is_frontline - True if battalion is on frontline
   */
  setFrontlineStatus(is_frontline) {
    this.is_frontline = is_frontline;
  }
  
  // ============================================================================
  // SERIALIZATION (enhanced for v2.0)
  // ============================================================================
  
  /**
   * Convert to JSON for storage
   * Includes battalion-specific fields and v2.0 enhancements
   * 
   * @returns {Object} JSON representation
   */
  toJSON() {
    return {
      ...super.toJSON(),
      battalion_number: this.battalion_number,
      parent_brigade_id: this.parent_brigade_id,
      position_anchor: this.position_anchor,
      
      // v2.0: Unit type and operational state
      unit_type: this.unit_type,
      unit_type_label: this.unit_type_config.label,
      unit_type_icon: this.unit_type_config.icon,
      unit_type_modifier: this.unit_type_modifier,
      has_frontage: this.has_frontage,
      
      operational_state: this.operational_state,
      operational_state_label: this.operational_state_config.label,
      dnbi_rate: this.getDNBIRate(),
      
      // Legacy fields
      role1_facility_id: this.role1_facility_id,
      mission: this.mission,
      is_frontline: this.is_frontline,
      
      // Calculated modifier
      casualty_modifier: this.getCasualtyModifier()
    };
  }
  
  /**
   * Get detailed summary including medical and operational status
   * 
   * @returns {Object} Detailed summary
   */
  getDetailedSummary() {
    const base_summary = this.getSummary();
    
    return {
      ...base_summary,
      battalion_number: this.battalion_number,
      
      // v2.0: Unit type info
      unit_type: this.unit_type,
      unit_type_label: this.getUnitTypeLabel(),
      unit_type_modifier: this.unit_type_modifier,
      has_frontage: this.has_frontage,
      
      // v2.0: Operational state info
      operational_state: this.operational_state,
      operational_state_label: this.getOperationalStateLabel(),
      dnbi_rate: this.getDNBIRate(),
      
      // Legacy fields
      mission: this.mission,
      is_frontline: this.is_frontline,
      casualty_modifier: this.getCasualtyModifier(),
      
      // Medical
      medical_support: this.hasMedicalSupport(),
      medical_capacity: this.getMedicalCapacityStatus(),
      role1_facility: {
        id: this.role1_facility_id,
        name: this.generateFacilityName(),
        distance_feba_km: this.getRole1Distance()
      }
    };
  }
  
  /**
   * Create Battalion from JSON data
   * Static factory method for deserialization
   * 
   * @param {Object} json - JSON data
   * @returns {Battalion} New Battalion instance
   */
  static fromJSON(json) {
    return new Battalion({
      id: json.id,
      name: json.name,
      battalion_number: json.battalion_number,
      unit_type: json.unit_type,
      operational_state: json.operational_state,
      strength: json.strength,
      position_feba_km: json.position_feba_km,
      mission: json.mission,
      is_frontline: json.is_frontline,
      parent_brigade_id: json.parent_brigade_id,
      position_anchor: json.position_anchor,
      nationalStandard: json.nationalStandard
    });
  }
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get all battalion unit types for dropdown
 * @returns {Array} Array of unit type options
 */
export function getBattalionUnitTypeOptions() {
  return Object.entries(BATTALION_UNIT_TYPES).map(([key, config]) => ({
    value: key,
    label: `${config.icon} ${config.label}`,
    ...config
  }));
}

/**
 * Get battalion unit types by category
 * @returns {Object} Categorized unit types
 */
export function getBattalionUnitTypesByCategory() {
  return {
    COMBAT: ['INFANTRY', 'MECHANIZED', 'ARMOR', 'RECONNAISSANCE'],
    COMBAT_SUPPORT: ['ARTILLERY', 'AIR_DEFENSE', 'ENGINEERS'],
    COMBAT_SERVICE_SUPPORT: ['LOGISTICS', 'SIGNAL', 'MEDICAL', 'HQ']
  };
}

/**
 * Check if battalion type is combat (holds frontage)
 * @param {string} unitType - Battalion unit type
 * @returns {boolean} True if combat type
 */
export function isCombatBattalion(unitType) {
  const config = getBattalionUnitType(unitType);
  return config.has_frontage === true;
}

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

export default Battalion;

/**
 * USAGE EXAMPLES:
 * 
 * // Create an infantry battalion
 * const infantry = new Battalion({
 *   id: 'bn1',
 *   name: '1st Infantry Battalion',
 *   battalion_number: 1,
 *   unit_type: 'INFANTRY',  // NEW v2.0
 *   strength: 800
 * });
 * 
 * console.log(infantry.unit_type);           // 'INFANTRY'
 * console.log(infantry.operational_state);    // 'COMBAT_ENGAGED' (auto-assigned)
 * console.log(infantry.getCasualtyModifier()); // 1.2 × mission × frontline modifiers
 * console.log(infantry.getDNBIRate());        // 2.0 (per 1000/day)
 * 
 * // Create an artillery battalion
 * const artillery = new Battalion({
 *   id: 'bn4',
 *   name: '4th Artillery Battalion',
 *   battalion_number: 4,
 *   unit_type: 'ARTILLERY'
 * });
 * 
 * console.log(artillery.unit_type);           // 'ARTILLERY'
 * console.log(artillery.operational_state);    // 'SUPPORT_ACTIVE' (auto-assigned)
 * console.log(artillery.has_frontage);        // false
 * console.log(artillery.position_feba_km);    // 15 (auto from unit type)
 * console.log(artillery.getCasualtyModifier()); // 0.08
 * 
 * // Change unit type
 * infantry.setUnitType('MECHANIZED');
 * console.log(infantry.getCasualtyModifier()); // 1.0 (now mechanized)
 * 
 * // Override operational state
 * infantry.setOperationalState('TRUE_RESERVE');
 * console.log(infantry.getDNBIRate());        // 0.5 (reserve rate)
 */
